import {
  BaseKey,
  IResourceComponentsProps,
  useCreate,
  useDelete,
  useExport,
  useGetIdentity,
  useMany,
  useNavigation,
  useUpdate,
  useShow,
  useOne,
  useUpdateMany,
} from '@pankod/refine-core'
import {
  List,
  RefreshButton,
  CreateButton,
  ExportButton,
  Table,
  Typography,
  Button,
  Space,
  EditButton,
  DeleteButton,
  DateField,
  getDefaultSortOrder,
  useModalForm,
  useForm,
  Icons,
  Popconfirm,
  Tag,
  ShowButton,
  Input,
  Divider,
  useModal,
  Select,
  Modal,
  useTable,
  Form,
  Col,
  Row,
  Menu,
  message,
} from '@pankod/refine-antd'
import { TableColumnsType } from 'antd'

import { IPaper, IPaperRequest, IPaperTemplate, ITestTrack, IFlaggedQuestion, ICleanupWindowQuestions } from 'interfaces'
import { EditPaper } from 'pages/papers'
import metaData from 'metadata/paper'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useUserContext } from 'context/user'
import paperVariant from 'helpers/paperVariant'
import sendEmail from 'sendmail'
import Permissions from 'permissions'
import { getCount } from 'gql/query'
import { autoGenerateQuestions } from 'helpers/paper/autoGenerateQuestion'
import PaperCreateModal from './paper_create_modal'
import CreatePaperNoEventModal from './create_paper_no_event_modal'
import dayjs from 'dayjs'
import { updateVisibiltityStatus } from 'gql/mutations'
import { generateTrackBasedPaperName } from 'helpers/paper/paperRequestName'

const { PlusOutlined } = Icons
const { Text } = Typography

interface PaperListProps extends IResourceComponentsProps {
  request?: IPaperRequest
}
type Permission = {
  granted?: boolean
}

interface CountType {
  approvedPaperCount: number
  forApprovelPaperCount: number
  requireAmendingPaperCount: number
  unfilledPaperCount: number
  totalCount: number
}

export const PapersTableList: React.FC<PaperListProps> = ({ request }) => {
  const { data: user } = useGetIdentity()
  const role = user?.defaultRole

  const { userRole, paperSetterList, userLastSearch, updateUserLastSearch } = useUserContext()

  const { create, edit, show, push } = useNavigation()
  // const navigate = useNavigate()
  const [paperCount, setPaperCount] = useState<number>(1)
  const [filteredPaperList, setFilteredPaperList] = useState<IPaper[] | undefined>()

  const { mutateAsync: createPaper } = useCreate<IPaper>()
  const { mutate: deletepaper } = useDelete<IPaper>()
  const { mutateAsync: updatePaper } = useUpdate<IPaper>()
  const { mutate: updatePaperRequestStatus } = useUpdate<IPaperRequest>()
  const [parentPaperData, setParentPaperData] = useState<IPaper>()
  const [hasPagination, setHasPagination] = useState<any>(true)
  const [newPaperSetterId, setNewPaperSetterId] = useState<string>('')
  const [selectedPaper, setSelectedPaper] = useState<string | undefined>()
  const [papersCount, setPapersCount] = useState<CountType>({
    approvedPaperCount: 0,
    forApprovelPaperCount: 0,
    requireAmendingPaperCount: 0,
    unfilledPaperCount: 0,
    totalCount: 0,
  })

  // For direct paper creation (no test event)
  const { modalProps: createPaperNoEventModalProps, show: showCreatePaperNoEventModal, close: closeCreatePaperNoEventModal } = useModal()
  const { formProps: createPaperNoEventFormProps } = useForm()
  const [createdPaperRequest, setCreatedPaperRequest] = useState<IPaperRequest | null>(null)
  const [showPaperCreateModalAfterRequest, setShowPaperCreateModalAfterRequest] = useState(false)

  //access control
  const [readPermission, setReadPermission] = useState<Permission>({})
  const [createPermission, setCreatePermission] = useState<Permission>({})
  const [editPermission, setEditPermission] = useState<Permission>({})
  const [deletePermission, setDeletePermission] = useState<Permission>({})

  //finding count of papers
  const findCount = async () => {
    try {
      let userId = user?.id
      let isAdmin = userRole === 'admin'
      let userrole = userRole
      const resource = 'papers'
      const responseFindCountPapers: any = await getCount({ userId, isAdmin, userrole, resource })
      const approvedPaperCount = responseFindCountPapers?.approvedCount?.aggregate?.count
      const forApprovelPaperCount = responseFindCountPapers?.forApprovelCount?.aggregate?.count
      const requireAmendingPaperCount = responseFindCountPapers?.requireAmendingCount?.aggregate?.count
      const unfilledPaperCount = responseFindCountPapers?.unfilledPaperCount?.aggregate?.count
      let totalCount = approvedPaperCount + forApprovelPaperCount + requireAmendingPaperCount + unfilledPaperCount
      setPapersCount({
        approvedPaperCount: approvedPaperCount,
        forApprovelPaperCount: forApprovelPaperCount,
        requireAmendingPaperCount: requireAmendingPaperCount,
        unfilledPaperCount: unfilledPaperCount,
        totalCount,
      })
    } catch (err) {
      console.log(err, 'error')
    }
  }

  const ifConfirm = async (e: React.MouseEvent<HTMLElement>, id: string) => {
    await updatePaper(
      {
        resource: 'papers',
        id: id,
        values: { active: false },
      },
      {
        onSuccess: () => {
          findCount()
        },
      }
    )
  }

  useEffect(() => {
    if (userRole !== undefined) {
      setReadPermission(Permissions.can(userRole).readAny('papers'))
      setCreatePermission(Permissions.can(userRole).createAny('papers'))
      setEditPermission(Permissions.can(userRole).updateAny('papers'))
      setDeletePermission(Permissions.can(userRole).deleteAny('papers'))
    }
    //handling search presistence
    if (userLastSearch?.paper && !request?.id) {
      searchFormProps.form?.setFieldsValue({
        paper_smart_id: userLastSearch.paper,
      })
      searchFormProps.form?.submit()
    }
  }, [userRole])

  useEffect(() => {
    if (!readPermission?.granted && readPermission?.granted !== undefined) {
      push('/')
    } else {
      findCount()
    }
  }, [readPermission])

  let isRequest = window.location.pathname.includes('paper_requests')
  const { filters, setFilters, sorter, tableQueryResult, searchFormProps, tableProps } = useTable<IPaper>({
    resource: 'papers',
    permanentFilter: request?.id
      ? [
          { field: 'paper_request_id', operator: 'eq', value: request?.id },
          { field: 'active', operator: 'eq', value: true },
          { field: 'in_use', operator: 'eq', value: true },
        ]
      : [
          { field: 'active', operator: 'eq', value: true },
          { field: 'in_use', operator: 'eq', value: true },
        ],
    initialSorter: [
      {
        field: 'created_at',
        order: 'desc',
      },
    ],
    hasPagination,
    metaData: metaData,
    onSearch: (data: any) => {
      const searchValue = data?.paper_smart_id?.trim()
      updateUserLastSearch('paper', searchValue || '')
      if (!searchValue) {
        const updatedFilters = (filters ?? []).filter((f: any) => f.meta !== 'search')
        setFilters(updatedFilters, 'replace')
        return []
      }
      if (!isNaN(Number(searchValue))) {
        return [
          {
            field: 'paper_id',
            operator: 'eq',
            value: Number(searchValue),
            meta: 'search',
          },
        ]
      }
      return [
        {
          operator: 'or',
          value: [
            {
              field: 'name',
              operator: 'contains',
              value: `%${searchValue}%`,
            },
            {
              field: 'paper_smart_id',
              operator: 'contains',
              value: `%${searchValue}%`,
            },
          ],
          meta: 'search',
        },
      ]
    },
  })


  const { tableQueryResult: activePaperCreated } = useTable<IPaper>({
    resource: 'papers',
    permanentFilter: request?.id
      ? [
          { field: 'paper_request_id', operator: 'eq', value: request?.id },
          { field: 'created_by', operator: 'eq', value: user?.id },
          { field: 'active', operator: 'eq', value: true },
        ]
      : [],
    metaData,
    queryOptions: { enabled: !!request?.id },
  })

  const activePaperCount = activePaperCreated?.data?.total

  // const { queryResult: paperRequestData } = useShow<IPaperRequest>({ metaData: { fields: ['id', 'status'] } })
  // const record = paperRequestData?.data?.data

  const { data: parentPaperResult } = useOne<IPaper>({
    id: request?.parent_paper_id!,
    resource: 'papers',
    metaData: metaData,
    queryOptions: { enabled: !!request?.parent_paper_id },
  })
  const listOfQuestions = parentPaperResult?.data?.subSectionQuestions?.flatMap((eachSub: any) => eachSub?.questionIds)
  const { tableQueryResult: flaggedQueryResult } = useTable<IFlaggedQuestion>({
    resource: 'flagged_questions',
    metaData: { fields: ['id', 'question_id'] },
    permanentFilter: [{ field: 'question_id', operator: 'in', value: listOfQuestions }],
    queryOptions: { enabled: listOfQuestions !== undefined && listOfQuestions?.length > 0 && !!request?.id },
  })

  const { show: showCleanupModal, close: closeCleanupModal, modalProps: cleanupModalProps } = useModal()
  const { tableQueryResult: cleanupQueryResult } = useTable<ICleanupWindowQuestions>({
    resource: 'cleanup_window_questions',
    metaData: {
      fields: [
        'id',
        'question_id',
        'paper_id',
        { question: ['id', 'question_id', 'title', 'status', { question_type: ['id', 'name'] }, { subject: ['name'] }, { work_request: ['name'] }] },
      ],
    },
    queryOptions: { enabled: !!cleanupModalProps?.visible && !!selectedPaper },
    permanentFilter: [
      { field: 'in_cleanup', operator: 'eq', value: true },
      { field: 'paper_id', operator: 'eq', value: selectedPaper },
    ],
  })

  const { show: showPsChangeModal, close: closePsChangeModal, modalProps: psChangeModalProps } = useModal()


useEffect(() => {
  // If request.id exists → remove search and apply request filter
  if (request?.id) {
    updateUserLastSearch('paper', '')

    searchFormProps.form?.setFieldsValue({
      paper_smart_id: '',
    })
    setFilters(
      [
        {
          field: 'paper_request_id',
          operator: 'eq',
          value: request.id,
        },
      ],
    )
    return
  }
  // Otherwise → remove ONLY search filters
  const cleanedFilters = (filters ?? []).filter(
    (f: any) => f.meta !== 'search'
  )
  setFilters(cleanedFilters, 'replace')
}, [request?.id])



  useEffect(() => {
    if (userRole !== undefined || userRole !== '') {
      if (userRole === 'papersetter') {
        setFilters([{ field: 'current_paper_setter_id', operator: 'eq', value: user?.id }], 'replace')
      }
      if (userRole === 'paperapprover') {
        setFilters(
          [
            { field: 'paper_approver_id', operator: 'eq', value: user?.id },
            {
              operator: 'or',
              value: [
                { field: 'status', operator: 'eq', value: 'Approved' },
                { field: 'status', operator: 'eq', value: 'For Approval' },
              ],
            },
          ],
          'replace'
        )
      }
      if (userRole === 'productmanager') {
        setFilters(
          [
            // {
            //   field: 'paper_request.created_by',
            //   operator: 'eq',
            //   value: user?.id,
            // },
          ],
          'replace'
        )
      }
    }
  }, [userRole])

  const handleChangePaperSetter = async () => {
    const activePaper = tableQueryResult?.data?.data?.[0]
    const paperSetterDetails = paperSetterList?.filter(eachUser => eachUser?.id === newPaperSetterId)[0]

    const mail_arr = []
    mail_arr.push({
      subject: 'You have been assigned as the new Paper Setter for this ongoing paper .',
      to: [{ name: `${paperSetterDetails?.displayName}`, email: `${paperSetterDetails?.email}` }],
      htmlContent: `<html><body>Hi,<br/><h4> Pending tasks of Paper ${activePaper?.paper_smart_id} has been assigned to you. For more information, Login to <a href="${process.env.REACT_APP_PRODUCTION_URL}" target='_blank'>centa ams</a> .</h4></body></html>`,
    })
    await updatePaper({ resource: 'papers', id: activePaper?.id!, values: { current_paper_setter_id: paperSetterDetails?.user?.id } })
    await sendEmail(mail_arr)
    closePsChangeModal()
  }

  const handleCreatePaper = async () => {
    updatePaperRequestStatus({ resource: 'paper_requests', values: { status: 'In Progress' }, id: request?.id! })
    const paperName = await generateTrackBasedPaperName({
      testTrackId: request?.test_track_id,
      testTrackName: request?.test_track?.name,
      productType: request?.product?.type,
    })
    const createdRecord = await createPaper({
      resource: 'papers',
      metaData,
      values: {
        status: 'Unfilled Paper',
        created_by: user?.id,
        current_paper_setter_id: user?.id,
        paper_smart_id: `pid-${request?.product?.product_id}-ttid-${request?.test_track?.test_track_id}-teid-${request?.test_event?.test_event_id}`,
        paper_template_id: request?.paper_template_id,
        test_event_id: request?.test_event_id,
        product_id: request?.product_id,
        test_track_id: request?.test_track_id,
        paper_request_id: request?.id,
        paper_approver_id: request?.paper_approver_id,
        slot_data: request?.slot_data,
        name: paperName,
      },
    })
    const pickQuestions = await autoGenerateQuestions(createdRecord.data, user)
    if (pickQuestions?.status) {
      const allSubsectionsWithAllQuestionIds = pickQuestions.allSubSectionsWithQuestionIds
      console.log(allSubsectionsWithAllQuestionIds, 'All Subsection and Questions')
      const questionAndAnswers = pickQuestions?.questionsAndAnswers
      console.log(questionAndAnswers, 'Question & Answers')
      const isUpdated = await updatePaper({
        resource: 'papers',
        id: createdRecord?.data?.id,
        values: { questionAnswerKeys: questionAndAnswers, subSectionQuestions: allSubsectionsWithAllQuestionIds },
      })
      if (isUpdated.data) {
        message.success('successfully created paper with automated questions')
      }
    }
    edit('papers', createdRecord?.data?.id)
  }

  const handleCreateVariantPaper = async () => {
    updatePaperRequestStatus({ resource: 'paper_requests', values: { status: 'In Progress' }, id: request?.id! })
    const parentPaperData = parentPaperResult?.data
    const flaggedQuestionList = flaggedQueryResult?.data?.data?.map(question => question?.question_id)
    const newPaperData = paperVariant({ parentPaperData, flaggedQuestionList }) // filtered data from parent paper

    const paperName = await generateTrackBasedPaperName({
      testTrackId: request?.test_track_id,
      testTrackName: request?.test_track?.name,
      productType: request?.product?.type,
    })
    const createdRecord = await createPaper({
      resource: 'papers',
      metaData,
      values: {
        status: 'Unfilled Paper',
        created_by: user?.id,
        current_paper_setter_id: user?.id,
        paper_smart_id: `pid-${request?.product?.product_id}-ttid-${request?.test_track?.test_track_id}-teid-${request?.test_event?.test_event_id}`,
        paper_template_id: request?.paper_template_id,
        test_event_id: request?.test_event_id,
        product_id: request?.product_id,
        test_track_id: request?.test_track_id,
        paper_request_id: request?.id,
        paper_approver_id: request?.paper_approver_id,
        slot_data: request?.slot_data,
        subSectionQuestions: newPaperData?.subSectionQuestions,
        questionAnswerKeys: newPaperData?.questionAnswerKeys,
        flagged_questions: newPaperData?.flagged_questions,
        approverFeedback: newPaperData?.approverFeedback,
        rejectedQuestions: newPaperData?.rejectedQuestions,
        parent_paper_id: newPaperData?.id,
        name: paperName,
      },
    })
    edit('papers', createdRecord?.data?.id)
  }

  const handleTest = () => {
    console.log('parent', parentPaperResult)
    const parentPaperData = parentPaperResult?.data
    const flaggedQuestionList = flaggedQueryResult?.data?.data?.map(question => question?.question_id)
    const newPaperData = paperVariant({ parentPaperData, flaggedQuestionList }) // filtered data from parent paper
  }

  const handlePaperRequestCreated = (paperRequest: IPaperRequest) => {
    setCreatedPaperRequest(paperRequest)
    closeCreatePaperNoEventModal()
    setShowPaperCreateModalAfterRequest(true)
  }

  const handleStatusFilter = (value: any, reset: string) => {
    let status = ''
    if (value !== null) {
      if (value?.key === 'for_approval') status = 'For Approval'
      if (value?.key === 'require_amending') status = 'Require Amending'
      if (value?.key === 'approved') status = 'Approved'
      setFilters([{ field: 'status', operator: 'eq', value: status }])
    }
    if (value === null) {
      const statusIndex = filters?.findIndex((eachFilter: any) => eachFilter?.field === 'status')
      if (statusIndex !== undefined && statusIndex !== -1) {
        filters?.splice(statusIndex, 1)
        filters !== undefined && setFilters([...filters])
      }
    }
  }

  const hideDeleteButton = (record: IPaper): boolean => {
    if (record?.work_requests !== undefined) {
      const activeWrList = record?.work_requests?.filter(workRequest => {
        if (workRequest?.active === true) {
          return workRequest
        }
      })
      const shouldHide = activeWrList?.length > 0
      return shouldHide
    }
    return false
  }

  const hideEdit = (record: any): boolean => {
    if (userRole === 'papersetter' || userRole === 'admin') {
      return false
    } else if (userRole === 'paperapprover' && record?.status === 'For Approval') {
      return false
    }
    return true
  }

  const hidePsChangeButton = (): boolean | undefined => {
    if (request !== undefined && tableQueryResult?.data?.data !== undefined && (userRole === 'admin' || userRole === 'productmanager')) {
      if (request?.status !== 'In Progress') {
        return true
      }
      if (tableQueryResult?.data?.data?.length > 0) {
        return false
      }
    } else {
      return true
    }
    return true
  }

  // const onChange = (page: number, pageSize?: any): void => {
  //   setCurrent(page)
  // }
  const searchByName = (value: any) => {
    // setHasPagination(false)
    setFilters([
      {
        field: 'paper_smart_id',
        operator: 'contains',
        value: `%${value}%`,
      },
    ])
  }

  const cleanupWindowColumns: TableColumnsType<ICleanupWindowQuestions> = [
    {
      title: 'Question ID',
      dataIndex: '',
      render(value, record, index) {
        return (
          <Text code strong>
            #{record?.question?.question_id}
          </Text>
        )
      },
    },
    {
      title: 'Title',
      dataIndex: '',
      render(value, record, index) {
        return <Text ellipsis={{ tooltip: record?.question?.title }}>{record?.question?.title}</Text>
      },
    },
    {
      title: 'Question Type',
      dataIndex: '',
      render(value, record, index) {
        return <Text>{record?.question?.question_type?.name}</Text>
      },
    },
    {
      title: 'Subject',
      dataIndex: '',
      render(value, record, index) {
        return <Text>{record?.question?.subject?.name}</Text>
      },
    },
    {
      title: 'Status',
      dataIndex: '',
      render(value, record, index) {
        const status = record?.question?.status
        return (
          <Tag
            color={
              status === 'Published'
                ? 'green'
                : status === 'Rejected at Review' || status === 'Rejected at Approval' || status === 'Retired'
                ? 'volcano'
                : status === 'Open (Require Amending)' || status === 'For Review (Require Amending)'
                ? 'pink'
                : 'geekblue'
            }
          >
            {status}
          </Tag>
        )
      },
    },
    {
      title: 'Work Request',
      dataIndex: '',
      render(value, record, index) {
        const workRequestName = record?.question?.work_request?.name ?? 'Not from a work request'
        return <Text>{workRequestName}</Text>
      },
    },
  ]

  const columns: TableColumnsType<IPaper> = [
    {
      dataIndex: 'paper_smart_id',
      title: 'Id',

      render: (_, record: any) => (
        <ShowButton
          size="small"
          type="link"
          icon={null}
          recordItemId={record?.id}
          onClick={() => {
            show('papers', record?.id)
          }}
        >
          <Text
            style={{
              textAlign: 'left',
              color: '#6659FF',
              cursor: 'pointer',
            }}
            ellipsis={{ tooltip: `${record?.paper_id}-${record?.paper_smart_id}` }}
          >
            {`#${record?.paper_id}-${record?.paper_smart_id}`}
          </Text>
        </ShowButton>
      ),
    },
    {
      dataIndex: 'name',
      title: 'Name',
      width: 200,
      render: (_, record) => (
        <ShowButton
          size="small"
          type="link"
          icon={null}
          recordItemId={record?.id}
          onClick={() => {
            show('papers', record?.id)
          }}
        >
          <Text
            style={{
              textAlign: 'left',
              color: '#6659FF',
              cursor: 'pointer',
            }}
            ellipsis={{ tooltip: `{record?.name}` }}
          >
            {record?.name}
          </Text>
        </ShowButton>
      ),
    },
    { dataIndex: 'product', title: 'Product', render: (_, record) => record?.product?.name },
    { dataIndex: 'paper_template', title: 'Paper Template', render: (_, record) => record?.paper_template?.name },
    { dataIndex: 'test_event', title: 'Test Event', render: (_, record) => record?.test_event?.name },
    {
      dataIndex: 'status',
      title: 'Stage',
      // width: 100,
      className: 'ant-table-column-sort',
      render: (value, record) => <Tag color={value === 'Approved' ? 'green' : 'geekblue'}>{value}</Tag>,
    },
    {
      dataIndex: 'created_at',
      title: 'Created At',
      render: value => <DateField value={value} format="LLL" />,
      sorter: { multiple: 1 },
      defaultSortOrder: getDefaultSortOrder('created_at', sorter),
      showSorterTooltip: true,
    },
    {
      dataIndex: 'updated_at',
      title: 'Validity',
      width: 250,
      render: (value, details) => {
        if (details?.status === 'Approved') {
          const newDate = dayjs(value).add(90, 'day')
          const formattedDate = newDate.format('DD-MM-YYYY')
          const isExpired = dayjs().isAfter(newDate)
          return <Tag color={isExpired ? 'red' : 'green'}>{formattedDate}</Tag>
        }
        return `-`
      },
    },
    {
      dataIndex: 'available_for_use',
      title: 'Status',
      render: (value, record) => {
        if (record?.is_disabled === true) return <Tag color="red">Disabled</Tag>
        if (value === true && record?.status === 'Approved') return <Tag color="green">Active</Tag>
        if (value === true && record?.status !== 'Approved') return <Tag color="geekblue">Pending</Tag>
        if (value === false) return <Tag color="red">Inactive</Tag>
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, record) => (
        <Space>
          <EditButton
            size="small"
            type="link"
            icon={null}
            onClick={() => edit('papers', record.id)}
            disabled={
              record?.status === 'Approved' ||
              (userRole === 'papersetter' && record?.status === 'For Approval') ||
              record?.paper_request?.status === 'Closed'
            }
            hidden={hideEdit(record)}
          >
            Edit
          </EditButton>
          <Popconfirm
            title="Are you sure to delete this task?"
            onConfirm={(e: any) => ifConfirm(e, record.id)}
            // onConfirm={async () => await updatePaper({ id: record.id, resource: 'papers', values: { active: false } })}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              style={{ color: 'red' }}
              type="link"
              hidden={hideDeleteButton(record) || record?.status === 'Approved' || (userRole !== 'papersetter' && userRole !== 'admin')}
            >
              Delete
            </Button>
          </Popconfirm>
          {userRole === 'productmanager' && record?.status === 'Approved' && (
            <Popconfirm
              title={!record?.is_disabled ? 'Are you sure to disable this paper?' : 'Are you sure to enable this paper?'}
              onConfirm={async () => {
                await updateVisibiltityStatus([record.id], !record?.is_disabled, 'papers', 'id', 'is_disabled')
                tableQueryResult?.refetch()
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                size="small"
                icon={record?.is_disabled ? <Icons.EyeInvisibleFilled /> : <Icons.EyeFilled />}
                style={{ color: !record?.is_disabled ? '#9dd7f5' : '#ed5d40' }}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  useEffect(() => {
    if (tableQueryResult?.data) setPaperCount(tableQueryResult?.data?.total)
  }, [tableQueryResult?.data])

  const reloadPage = () => {
    tableQueryResult?.refetch()
  }

  return (
    <>
      <List
        title="Papers"
        pageHeaderProps={{
          extra: [
            //  <ExportButton onClick={triggerExport} loading={isLoading} />,
            <RefreshButton onClick={() => tableQueryResult?.refetch()} />,
            <>
              {request?.request_type === 'variant' ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleCreateVariantPaper()}
                  disabled={paperCount > 0 || request?.status === 'Closed'}
                  hidden={!request?.id || !(userRole === 'papersetter')}
                >
                  Create Variant Paper
                </Button>
              ) : (
                <>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleCreatePaper()}
                    disabled={
                      paperCount > 0 ||
                      request?.status === 'Closed' ||
                      (userRole === 'papersetter' && activePaperCount !== undefined && activePaperCount > 0)
                    }
                    hidden={!request?.id || !(userRole === 'papersetter')}
                  >
                    Create Paper
                  </Button>
                  {request?.id &&
                    userRole === 'papersetter' &&
                    paperCount <= 0 &&
                    request?.status !== 'Closed' &&
                    (activePaperCount === undefined || activePaperCount <= 0) && (
                      <PaperCreateModal request={request} userId={user?.id} refresh={() => tableQueryResult?.refetch()} />
                    )}
                </>
              )}
              {!request?.id && userRole === 'productmanager' && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => showCreatePaperNoEventModal()}
                >
                  Create Paper (Direct)
                </Button>
              )}
            </>,
            <Button
              hidden={hidePsChangeButton()}
              onClick={() => {
                showPsChangeModal()
              }}
            >
              Change PS
            </Button>,
            !!!request && userRole === 'papersetter' && (
              <Button
                onClick={() => {
                  showCleanupModal()
                }}
              >
                Cleanup Questions
              </Button>
            ),
          ],
        }}
      >
        {request===undefined&&<div>
        <Divider />
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <div
              style={{
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                padding: '1rem',
                height: '4rem',
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <h4>
                {' '}
                Total Count : <span style={{ fontSize: '16px', marginLeft: '5px', fontWeight: 'bold' }}>{papersCount?.totalCount}</span>
              </h4>
            </div>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <div
              style={{
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                padding: '1rem',
                height: '4rem',
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <h4>
                {' '}
                Approved Papers : <span style={{ fontSize: '16px', marginLeft: '5px', fontWeight: 'bold' }}>{papersCount?.approvedPaperCount}</span>
              </h4>
            </div>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={6}>
            <div
              style={{
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                padding: '1rem',
                height: '4rem',
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <h4>
                {' '}
                For Approval Papers :{' '}
                <span style={{ fontSize: '16px', marginLeft: '5px', fontWeight: 'bold' }}>{papersCount?.forApprovelPaperCount}</span>
              </h4>
            </div>
          </Col>
          {userRole !== 'paperapprover' && (
            <Col xs={24} sm={12} md={12} lg={8} xl={6}>
              <div
                style={{
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                  padding: '1rem',
                  height: '4rem',
                  borderRadius: '10px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <h4>
                  Amending Required Papers :{' '}
                  <span style={{ fontSize: '16px', marginLeft: '5px', fontWeight: 'bold' }}>{papersCount?.requireAmendingPaperCount}</span>
                </h4>
              </div>
            </Col>
          )}
          {userRole !== 'paperapprover' && (
            <Col xs={24} sm={12} md={12} lg={8} xl={6}>
              <div
                style={{
                  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                  padding: '1rem',
                  height: '4rem',
                  borderRadius: '10px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <h4>
                  {' '}
                  Unfilled Papers : <span style={{ fontSize: '16px', marginLeft: '5px', fontWeight: 'bold' }}>{papersCount?.unfilledPaperCount}</span>
                </h4>
              </div>
            </Col>
          )}
        </Row>
        <Divider />
       </div>}
        
        {/* <Input.Search
        placeholder="Search By Name"
        onChange={e => {
          if (e.type === 'click') {
            setHasPagination(true)
          }
        }}
        allowClear
        style={{ width: '100%' }}
        onSearch={(value: any) => searchByName(value)}
      /> */}
        {!!request?.id ? (
          <></>
        ) : (
          <Col span={8} flex="start">
            <Row gutter={[6, 16]}>
              <Menu
                mode="horizontal"
                style={{ backgroundColor: 'white' }}
                onClick={value => {
                  handleStatusFilter(value, '')
                }}
              >
                <Menu.SubMenu
                  key="all_papers"
                  icon={<Icons.AppstoreOutlined />}
                  title="All Papers"
                  onTitleClick={() => {
                    handleStatusFilter(null, 'reset')
                  }}
                ></Menu.SubMenu>
                <Menu.SubMenu key="status" title="Stage" icon={<Icons.FilterOutlined />}>
                  <Menu.Item key="require_amending" title="Require Amending" icon={<Icons.AppstoreOutlined />}>
                    Return For Amending
                  </Menu.Item>
                  <Menu.Item key="for_approval" title="For Approval" icon={<Icons.AppstoreOutlined />}>
                    For Approval
                  </Menu.Item>
                  <Menu.Item key="approved" title="Approved" icon={<Icons.AppstoreOutlined />}>
                    Approved
                  </Menu.Item>
                </Menu.SubMenu>
              </Menu>
            </Row>
            {/* <Row gutter={[24, 16]}>
          <Col  xl={24}> */}
            <Form {...searchFormProps} style={{ marginTop: '1rem' }}>
              <Space>
                <Form.Item name="paper_smart_id">
                  <Input
                    placeholder="Search by Smart ID"
                    suffix={
                      <>
                        <Icons.SearchOutlined style={{ cursor: 'pointer' }} onClick={searchFormProps.form?.submit} />
                        <Icons.CloseCircleOutlined
                          style={{ cursor: 'pointer', marginLeft: '6px' }}
                          onClick={() => {
                            searchFormProps?.form?.setFieldsValue({ paper_smart_id: '' })
                            searchFormProps.form?.submit()
                          }}
                        />
                      </>
                    }
                  />
                </Form.Item>
              </Space>
            </Form>

            {/* </Col>
          </Row> */}
          </Col>
        )}

        <Divider />
        <Table
          {...tableProps}
          // dataSource={tableQueryResult?.data?.data}
          rowKey="id"
          size="small"
          // loading={tableQueryResult?.isLoading}
          columns={columns}
          // pagination={{ onChange, total: tableQueryResult?.data?.total }}
        />
        <Modal
          {...psChangeModalProps}
          title="Change Paper Setter"
          footer={
            <>
              <Button
                onClick={() => {
                  handleChangePaperSetter()
                }}
              >
                Save
              </Button>
            </>
          }
        >
          <Space>
            <Text strong>Select New Paper Setter:</Text>
            <Select
              style={{ width: 200 }}
              onChange={value => setNewPaperSetterId(value)}
              defaultValue={tableQueryResult?.data?.data?.[0]?.current_paper_setter_id}
            >
              {paperSetterList !== undefined &&
                paperSetterList.length > 0 &&
                paperSetterList?.map((item: any) => (
                  <option key={item?.id} value={item?.id}>
                    {item?.displayName}
                  </option>
                ))}
            </Select>
          </Space>
        </Modal>
        <Modal
          {...cleanupModalProps}
          title="Cleanup Window Questions"
          width={'60%'}
          footer={
            <>
              <Button
                onClick={() => {
                  closeCleanupModal()
                }}
              >
                Close
              </Button>
            </>
          }
        >
          <Row gutter={[16, 48]}>
            <Col span={8}>
              <Select
                placeholder="Select a paper"
                size="middle"
                style={{ width: '100%' }}
                onChange={value => {
                  setSelectedPaper(value)
                }}
              >
                {tableQueryResult?.data?.data?.map((paper, idx) => (
                  <option key={idx} value={paper?.id}>
                    #{paper?.paper_id} {paper?.paper_smart_id}
                  </option>
                ))}
              </Select>
            </Col>
            <Col span={24}>
              <Table
                title={() => <Typography.Title level={4}>List of Questions</Typography.Title>}
                dataSource={cleanupQueryResult?.data?.data}
                columns={cleanupWindowColumns}
                rowKey={'id'}
              />
            </Col>
          </Row>
        </Modal>
      </List>

      {/* Create Paper without Test Event Modal */}
      <CreatePaperNoEventModal
        modalProps={createPaperNoEventModalProps}
        formProps={createPaperNoEventFormProps}
        close={closeCreatePaperNoEventModal}
        onPaperRequestCreated={handlePaperRequestCreated}
      />

      {/* Conditionally show PaperCreateModal after paper request is created */}
      {showPaperCreateModalAfterRequest && createdPaperRequest && (
        <PaperCreateModal
          request={createdPaperRequest}
          userId={user?.id}
          refresh={() => {
            tableQueryResult?.refetch()
            setCreatedPaperRequest(null)
            setShowPaperCreateModalAfterRequest(false)
          }}
        />
      )}
    </>
  )
}
